import {
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  SimpleGrid,
  Spinner,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { CertificatesManagement } from '@texas/components/article/manage/CertificatesManagement';
import { BodyOverride } from '@texas/components/shared/BodyOverride';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { EntityDirectoryFilterBar } from '@texas/components/shared/entityDirectory/EntityDirectoryFilterBar';
import { EntityDirectoryPage } from '@texas/components/shared/entityDirectory/EntityDirectoryPage';
import { TexasSelect } from '@texas/components/shared/form/TexasSelect';
import { EntityDirectoryEmptyState } from '@texas/components/shared/entityDirectory/EntityDirectoryEmptyState';
import { SearchFilterInput } from '@texas/components/SearchFilterInput';
import { ComplianceCoverageTable } from './ComplianceCoverageTable';
import { SupplierComplianceCoverageStatus } from '@texas/api/endpoints/variantsApi';
import { getEnumValues } from '@texas/utils/helpers/enumHelpers';
import { useVariantComplianceCoverage } from './useVariantComplianceCoverage';
import { complianceCoverageStatusLabel } from './translations';
import { useFilters } from './useFilters';

interface Props {
  articleId: number;
  variantId: number;
}

const statuses = getEnumValues<SupplierComplianceCoverageStatus>(
  SupplierComplianceCoverageStatus,
);

export function VariantComplianceCoverage({ articleId, variantId }: Props) {
  const { t } = useTranslation();
  const resource = useVariantComplianceCoverage(articleId, variantId);
  const editor = useDisclosure();
  const {
    filters,
    updateFilters,
    scopedBranches,
    effectiveBranchId,
    effectiveSupplierId,
    suppliers,
    branches,
  } = useFilters({ coverageBranches: resource.data?.branches, variantId });

  const branchOptions = scopedBranches.map((branch) => ({
    value: branch.id,
    label: `${branch.identifier} · ${branch.name}`,
  }));
  const supplierOptions = suppliers.map((supplier) => ({
    value: supplier.id,
    label: supplier.name,
  }));
  const statusOptions = statuses.map((status) => ({
    value: status,
    label: complianceCoverageStatusLabel(status, t),
  }));

  return (
    <BodyOverride>
      <EntityDirectoryPage
        title={t('supplierComplianceCoverage.title')}
        description={t('supplierComplianceCoverage.description')}
        actions={
          <Button
            variant="entityDirectorySecondary"
            onClick={editor.onOpen}
            isDisabled={!resource.data}
          >
            {t('supplierComplianceCoverage.editRequirements')}
          </Button>
        }
      >
        <EntityDirectoryFilterBar>
          <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} gap={3} w="full">
            <SearchFilterInput
              style="entityDirectory"
              value={filters.search}
              onChange={(search) => updateFilters({ search })}
              placeholder={t('supplierComplianceCoverage.search')}
            />
            <TexasSelect
              aria-label={t('supplierComplianceCoverage.allBranches')}
              placeholder={t('supplierComplianceCoverage.allBranches')}
              options={branchOptions}
              value={
                branchOptions.find(
                  (option) => option.value === effectiveBranchId,
                ) ?? null
              }
              onChange={(option) =>
                updateFilters({ branchId: option?.value ?? null })
              }
              isClearable={true}
            />
            <TexasSelect
              aria-label={t('supplierComplianceCoverage.allSuppliers')}
              placeholder={t('supplierComplianceCoverage.allSuppliers')}
              options={supplierOptions}
              value={
                supplierOptions.find(
                  (option) => option.value === effectiveSupplierId,
                ) ?? null
              }
              onChange={(option) =>
                updateFilters({ supplierId: option?.value ?? null })
              }
              isClearable={true}
            />
            <TexasSelect
              aria-label={t('supplierComplianceCoverage.allStatuses')}
              placeholder={t('supplierComplianceCoverage.allStatuses')}
              options={statusOptions}
              value={
                statusOptions.find(
                  (option) => option.value === filters.status,
                ) ?? null
              }
              onChange={(option) =>
                updateFilters({ status: option?.value ?? null })
              }
              isClearable={true}
            />
          </SimpleGrid>
        </EntityDirectoryFilterBar>
        {resource.loading && !resource.data && <Spinner alignSelf="center" />}
        {resource.error && <ErrorDetails error={resource.error} />}
        {resource.data && branches.length === 0 && (
          <EntityDirectoryEmptyState>
            {t('general.noResult')}
          </EntityDirectoryEmptyState>
        )}
        {resource.data &&
          branches.map((branch) => (
            <ComplianceCoverageTable key={branch.id} branch={branch} />
          ))}
        <Text variant="entity-directory-description">
          {t('supplierComplianceCoverage.scopeNote')}
        </Text>
      </EntityDirectoryPage>
      <Drawer isOpen={editor.isOpen} onClose={editor.onClose} size="xl">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>
            {t('supplierComplianceCoverage.editRequirements')}
          </DrawerHeader>
          <DrawerBody>
            {resource.data && (
              <CertificatesManagement
                articleId={articleId}
                customerId={resource.data.customerId}
              />
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </BodyOverride>
  );
}
